import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, tap } from 'rxjs';

import { FamilyCode, microControllersFamilies } from '#data/microControllersFamilies.data';
import { ControllerCode, MicroController } from '#models/MicroController.model';

import { CheckResult } from './CheckResult';
import {
  ControllerDetails,
  findControl,
  getEscalationContact,
  getReviewDate,
  hasCheckData,
  isEscalated,
  isExpiredAfterCheck,
} from './ControllerDetails';
import CveService from './cve.service';
import { VettingIteration } from './VettingIteration';

@Injectable()
export class InitFamilyService {
  private readonly cveService: CveService = inject(CveService);

  public microControllers: Partial<Record<ControllerCode, MicroController & { subFamilyI18n: string }>> = {};

  public isIterationClosed = false;
  private iterationId!: string;

  public microControllersList = () => Object.values(this.microControllers);
  public microControllersFromSubFamily = (subFamilyI18n: string): MicroController[] =>
    this.microControllersList().filter((control) => control.subFamilyI18n === subFamilyI18n);

  init(familyCode: FamilyCode) {
    this.cveService.iterationData$
      .pipe(
        tap((iteration) => {
          if (!iteration) return;
          this.iterationId = iteration.iterationId;
          this.isIterationClosed = !!iteration?.closure;
        }),
        map((iteration, index) => ({ iteration, index })),
      )
      .subscribe(({ iteration, index }) => {
        if (!iteration) return;
        if (index === 0) {
          this.initializeMicroControllers(familyCode, iteration.controls);
          this.microControllersList().forEach((control) =>
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.updateMicroControllerState(control.code),
          );
          return;
        }
        this.fillIterationMicroControllersStates(iteration);
      });
  }

  private initializeMicroControllers(familyCode: FamilyCode, controls: ControllerDetails[]) {
    const processResults: CheckResult[] = history.state?.['processResults'];

    for (const subFamily of microControllersFamilies[familyCode].subFamilies) {
      for (const controlConfig of subFamily.microControllers) {
        const controllerDetails = findControl(controls, controlConfig.code);
        if (!controllerDetails) {
          continue;
        }
        const control = {
          ...controlConfig,
          inError: !!processResults?.find(
            (result) => result.controlCode === controlConfig.code && result.statusCode === 'ERROR',
          ),
          controllerDetails,
          subFamilyI18n: subFamily.i18n,
        };
        this.microControllers[control.code] = control;
      }
    }
  }

  private fillIterationMicroControllersStates(iteration: VettingIteration) {
    for (const controllerDetails of iteration.controls.filter(
      (control) => !!this.microControllers[control.controlCode],
    )) {
      const control = this.microControllers[controllerDetails.controlCode];
      if (!control) continue;
      this.microControllers[controllerDetails.controlCode] = {
        ...control,
        controllerDetails,
      };
    }
  }

  private async updateMicroControllerState(controlCode: ControllerCode) {
    const control = this.microControllers[controlCode];
    if (!control) return;

    control.loadingError = undefined;
    control.isLoading = true;
    this.microControllers[controlCode] = control;

    try {
      const controllerDetails: ControllerDetails | undefined = await this.cveService.pollControllerState(
        this.iterationId,
        control.code,
      );
      if (!controllerDetails) return;
      if (hasCheckData(controllerDetails)) {
        control.isExpired = isExpiredAfterCheck(controllerDetails) && !isEscalated(controllerDetails);
        control.escalationContact = getEscalationContact(controllerDetails);
      }
      control.controllerDetails = controllerDetails;
      control.reviewDate = getReviewDate(controllerDetails);
      this.microControllers[controlCode] = control;
    } catch (error) {
      control.loadingError = {};
      if (error instanceof HttpErrorResponse) {
        control.loadingError.traceparent = error.headers.get('traceparent') ?? undefined;
      }
      this.microControllers[controlCode] = control;
    }

    control.isLoading = false;
    this.microControllers[controlCode] = control;
  }
}
